import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ColDef, GridApi, NewValueParams, RowDragEndEvent } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { firstValueFrom } from 'rxjs';

type ProfileType = 1 | 2 | 3 | 4;

export type ContactAccessProfile = {
  contact_access_profile_uuid: string;
  domain_uuid: string;
  contact_uuid: string | null;
  profile_type: ProfileType | null;
  entity_target: string;
  profile_name: string;
  is_default: boolean;
  active: boolean;
  field_config: any[];
  created: string;
  modified: string;
  created_by?: string | null;
  modified_by?: string | null;
  base_view: string;
};

export type FieldConfigItem = {
  db_field: string;
  input_type: 'text' | 'readonly_text' | 'number' | 'checkbox' | 'date' | 'datetime' | 'time' | 'modal';
  is_visible: boolean;
  header_name: string;
  is_editable: boolean;
  is_sortable: boolean;
  display_order: number;

  join_clause?: string;
  select_expr?: string;
  update_target?: { table: string; column: string; match_column?: string; scope_column?: string };
  validation?: { max_length?: number; allowed_values?: any[] };
  modal_config?: any;
};

export interface JoinConfigStep {
  type: 'LEFT JOIN' | 'LATERAL LATEST';
  target_table: string;
  alias: string;
  source_table: string;
  source_column: string;
  target_column: string;
  sort_column?: string;
}

type ApiResp<T = any> = {
  success?: boolean;
  message?: string;
  code?: number;
  data?: T;
  contact_access_profile_uuid?: string;
};

@Component({
  selector: 'app-contact-access-profiles',
  templateUrl: './contact-access-profiles.component.html',
  styleUrls: ['./contact-access-profiles.component.scss'],
})
export class ContactAccessProfilesComponent implements OnInit, OnDestroy {
  profiles: ContactAccessProfile[] = [];
  selectedProfile: ContactAccessProfile | null = null;

  profileForm!: FormGroup;

  fieldGridApi: GridApi<any> | null = null;
  fieldColumnDefs: ColDef[] = [];
  fieldRowData: FieldConfigItem[] = [];

  loading = false;
  saving = false;
  errorMessage = '';
  infoMessage = '';

  private readonly API_URL = '/api/dynamic_grids/contact_access_profiles.php';
  private readonly displayOrderStep = 10;
  private gridListenersAttached = false;

  // Visual Join Builder State
  joinBuilder = {
    isOpen: false,
    rowIndex: -1,
    field: null as FieldConfigItem | null,
    steps: [] as JoinConfigStep[]
  };

  constructor(private fb: FormBuilder, private http: HttpClient) {}

  ngOnInit(): void {
    this.buildForm();
    this.buildFieldGridDefs();
    this.refreshData();
  }

  ngOnDestroy(): void {
    this.detachGridListeners();
  }

  private buildForm() {
    this.profileForm = this.fb.group({
      profile_name: ['', [Validators.required]],
      entity_target: ['', [Validators.required]],
      base_view: ['', [Validators.required]],
      profile_type: [null as ProfileType | null],
      contact_uuid: [null as string | null],
      active: [true],
      is_default: [false],
    });
  }

  private buildFieldGridDefs() {
    this.fieldColumnDefs = [
      {
        headerName: '',
        colId: 'row_drag',
        width: 48,
        minWidth: 48,
        maxWidth: 48,
        pinned: 'left',
        rowDrag: true,
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: true,
        cellClass: 'cap-drag-cell',
        headerTooltip: 'Drag fields to reorder them',
      },
      {
        headerName: 'Order',
        field: 'display_order',
        width: 90,
        editable: false,
        sortable: false,
        filter: 'agNumberColumnFilter',
        cellClass: 'cap-order-cell',
      },
      { headerName: 'Header', field: 'header_name', flex: 1, minWidth: 160, editable: true },
      { headerName: 'db_field', field: 'db_field', flex: 1, minWidth: 260, editable: true },
      {
        headerName: 'Type',
        field: 'input_type',
        width: 150,
        editable: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: ['readonly_text', 'text', 'number', 'checkbox', 'date', 'datetime', 'time', 'modal'] },
      },
      {
        headerName: 'Visible',
        field: 'is_visible',
        width: 110,
        editable: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: [true, false] },
      },
      {
        headerName: 'Editable',
        field: 'is_editable',
        width: 110,
        editable: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: [true, false] },
      },
      {
        headerName: 'Sortable',
        field: 'is_sortable',
        width: 110,
        editable: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: [true, false] },
      },
      { headerName: 'select_expr', field: 'select_expr', flex: 1, minWidth: 220, editable: true },
      
      {
        headerName: 'join_clause',
        field: 'join_clause',
        flex: 1,
        minWidth: 220,
        editable: false,
        cellRenderer: (p: any) => {
          const dbField = p.data?.db_field || '';
          const [table] = dbField.split('.');
          const baseView = this.profileForm.get('base_view')?.value || '';

          if (!dbField || table === baseView) {
            const span = document.createElement('span');
            span.style.opacity = '0.3';
            span.innerText = '-';
            return span;
          }

          const wrapper = document.createElement('div');
          wrapper.style.display = 'flex';
          wrapper.style.alignItems = 'center';
          wrapper.style.justifyContent = 'space-between';
          wrapper.style.width = '100%';
          wrapper.style.height = '100%';

          const text = document.createElement('span');
          text.style.overflow = 'hidden';
          text.style.textOverflow = 'ellipsis';
          text.style.whiteSpace = 'nowrap';
          text.style.opacity = '0.7';
          
          const isConfigured = p.value && p.value.includes('/*CAP_JC');
          const hasRawSql = p.value && p.value.trim().length > 0 && !isConfigured;
          text.innerText = isConfigured ? '(Configured)' : (hasRawSql ? '(Raw SQL)' : '');

          const btn = document.createElement('button');
          btn.className = 'cap-btn cap-btn--tiny';
          btn.innerText = 'Build Join';
          btn.onclick = (e) => {
            e.stopPropagation();
            this.openJoinBuilder(p.node.rowIndex, p.data);
          };

          wrapper.appendChild(text);
          wrapper.appendChild(btn);
          return wrapper;
        }
      },
      {
        headerName: 'update_target',
        field: 'update_target',
        flex: 1,
        minWidth: 240,
        editable: true,
        valueFormatter: (p) => this.stringifyOrEmpty(p.value),
        valueParser: (p) => this.parseJsonOrKeepString((p as any).newValue),
      },
      {
        headerName: 'modal_config',
        field: 'modal_config',
        flex: 1,
        minWidth: 240,
        editable: true,
        valueFormatter: (p) => this.stringifyOrEmpty(p.value),
        valueParser: (p) => this.parseJsonOrKeepString((p as any).newValue),
      },
      {
        headerName: 'validation',
        field: 'validation',
        flex: 1,
        minWidth: 220,
        editable: true,
        valueFormatter: (p) => this.stringifyOrEmpty(p.value),
        valueParser: (p) => this.parseJsonOrKeepString((p as any).newValue),
      },
      {
        headerName: '',
        colId: 'actions',
        width: 90,
        pinned: 'right',
        sortable: false,
        filter: false,
        cellRenderer: (p: any) => {
          const btn = document.createElement('button');
          btn.className = 'cap-btn cap-btn--tiny';
          btn.textContent = 'Del';
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.removeFieldRow(p.node.rowIndex);
          });
          return btn;
        },
      },
    ];
  }

  onFieldGridReady(e: any) {
    this.fieldGridApi = e.api;
    this.attachGridListeners();
  }

  onFieldRowDragEnd(_: RowDragEndEvent): void {
    this.syncFieldRowDataFromGrid();
    this.renumberFieldDisplayOrders();
    this.applyFieldRowDataToGrid();
    this.infoMessage = 'Field order updated. Save the profile to keep it.';
  }

  private attachGridListeners() {
    if (!this.fieldGridApi || this.gridListenersAttached) return;
    this.fieldGridApi.addEventListener('cellValueChanged', this.onGridCellValueChanged);
    this.gridListenersAttached = true;
  }

  private detachGridListeners() {
    if (!this.fieldGridApi || !this.gridListenersAttached) return;
    this.fieldGridApi.removeEventListener('cellValueChanged', this.onGridCellValueChanged);
    this.gridListenersAttached = false;
  }

  // ───────────────────────────────────────────────────────────────
  // Schema Discovery & PK Inference
  // ───────────────────────────────────────────────────────────────
  private inferPrimaryKey(tableName: string): string {
    if (!tableName) return 'uuid';
    let base = tableName.startsWith('v_') ? tableName.substring(2) : tableName;
    if (base.endsWith('s')) base = base.substring(0, base.length - 1);
    return base + '_uuid';
  }

  getDynamicTables(): string[] {
    const tables = new Set<string>();
    const baseView = this.profileForm.get('base_view')?.value;
    if (baseView) tables.add(baseView);

    this.fieldRowData.forEach(row => {
      if (row.db_field && row.db_field.includes('.')) {
        tables.add(row.db_field.split('.')[0]);
      }
    });
    return Array.from(tables);
  }

  getDynamicColumns(tableName: string): string[] {
    if (!tableName) return [];
    const cols = new Set<string>();
    
    cols.add(this.inferPrimaryKey(tableName));
    const baseView = this.profileForm.get('base_view')?.value;
    if (baseView) cols.add(this.inferPrimaryKey(baseView));

    this.fieldRowData.forEach(row => {
      if (row.db_field && row.db_field.startsWith(tableName + '.')) {
        cols.add(row.db_field.split('.')[1]);
      }
    });
    return Array.from(cols);
  }

  // ───────────────────────────────────────────────────────────────
  // Business Logic & Auto-Generation
  // ───────────────────────────────────────────────────────────────
  private generateUpdateTarget(dbField: string): any {
    if (!dbField || !dbField.includes('.')) return null;
    const [table, column] = dbField.split('.');
    const baseView = this.profileForm.get('base_view')?.value || 'v_ladings';
    const matchCol = this.inferPrimaryKey(baseView);
    let scopeCol = undefined;
    if (table === 'v_lading_site_checks') scopeCol = 'contact_type';

    return { 
      table: table, 
      column: column, 
      match_column: matchCol,
      ...(scopeCol ? { scope_column: scopeCol } : {})
    };
  }

  private onGridCellValueChanged = (ev: NewValueParams) => {
    const data = ev.data;
    const colId = ev.column.getColId();

    if ((colId === 'db_field' || colId === 'is_editable') && data.is_editable && data.db_field) {
      if (!data.update_target || Object.keys(data.update_target).length === 0) {
        const generated = this.generateUpdateTarget(data.db_field);
        if (generated) {
          data.update_target = generated;
          ev.node?.setDataValue('update_target', generated); 
        }
      }
    }
  };

  // ───────────────────────────────────────────────────────────────
  // Visual Join Builder
  // ───────────────────────────────────────────────────────────────
  openJoinBuilder(rowIndex: number, field: FieldConfigItem) {
    this.joinBuilder.rowIndex = rowIndex;
    this.joinBuilder.field = field;
    this.errorMessage = '';

    const rawSql = field.join_clause || '';
    this.joinBuilder.steps = this.parseJoinClauseToSteps(rawSql);

    if (rawSql.trim().length > 0 && this.joinBuilder.steps.length === 0) {
      this.errorMessage = 'Warning: This field contains raw SQL without visual builder metadata. Building a new join here will overwrite the raw SQL.';
    } else if (this.joinBuilder.steps.length === 0) {
      this.addJoinStep();
    }
    
    this.joinBuilder.isOpen = true;
  }

  closeJoinBuilder() {
    this.joinBuilder.isOpen = false;
    this.joinBuilder.rowIndex = -1;
    this.joinBuilder.field = null;
    this.joinBuilder.steps = [];
    this.errorMessage = '';
  }

  addJoinStep() {
    const baseView = this.profileForm.get('base_view')?.value || 'v_ladings';
    const inferredBasePk = this.inferPrimaryKey(baseView);
    const prevTable = this.joinBuilder.steps.length > 0 
      ? (this.joinBuilder.steps[this.joinBuilder.steps.length - 1].alias || this.joinBuilder.steps[this.joinBuilder.steps.length - 1].target_table)
      : baseView;

    this.joinBuilder.steps.push({
      type: 'LEFT JOIN',
      target_table: '',
      alias: '',
      source_table: prevTable,
      source_column: inferredBasePk,
      target_column: inferredBasePk
    });
  }

  removeJoinStep(index: number) { 
    this.joinBuilder.steps.splice(index, 1); 
  }

  saveJoinBuilder() {
    if (!this.joinBuilder.field) return;
    const compiledSql = this.compileJoinConfig(this.joinBuilder.steps);
    const rowNode = this.fieldGridApi?.getDisplayedRowAtIndex(this.joinBuilder.rowIndex);
    
    if (rowNode) {
      rowNode.setDataValue('join_clause', compiledSql);
    } else {
      this.fieldRowData[this.joinBuilder.rowIndex].join_clause = compiledSql;
      this.applyFieldRowDataToGrid();
    }
    
    this.closeJoinBuilder();
  }

  compileJoinConfig(steps: JoinConfigStep[]): string {
    if (!steps || steps.length === 0) return '';
    const sqlParts = steps.map(s => {
      const alias = s.alias ? ` ${s.alias}` : '';
      const tName = s.alias ? s.alias : s.target_table;
      
      if (s.type === 'LATERAL LATEST') {
         const sortCol = s.sort_column || 'created';
         return `LEFT JOIN LATERAL (SELECT * FROM ${s.target_table} WHERE ${s.target_table}.${s.target_column} = ${s.source_table}.${s.source_column} ORDER BY ${sortCol} DESC LIMIT 1) ${tName} ON true`;
      }
      return `LEFT JOIN ${s.target_table}${alias} ON ${s.source_table}.${s.source_column} = ${tName}.${s.target_column}`;
    });
    
    const jsonState = JSON.stringify(steps);
    return `/*CAP_JC:${jsonState}*/ ` + sqlParts.join(' ');
  }

  parseJoinClauseToSteps(clause: string): JoinConfigStep[] {
    if (!clause) return [];
    const match = clause.match(/\/\*CAP_JC:(.*?)\*\//);
    if (match && match[1]) {
      try { return JSON.parse(match[1]); } catch (e) { console.error("Failed to parse embedded join state", e); }
    }
    return [];
  }

  previewCompiledSql(): string { 
    return this.compileJoinConfig(this.joinBuilder.steps); 
  }

  getPreviousTables(currentIndex: number): string[] {
    const tables = [this.profileForm.get('base_view')?.value || 'v_ladings'];
    for (let i = 0; i < currentIndex; i++) {
      tables.push(this.joinBuilder.steps[i].alias || this.joinBuilder.steps[i].target_table);
    }
    return tables;
  }

  // ───────────────────────────────────────────────────────────────
  // Loading & Selection
  // ───────────────────────────────────────────────────────────────
  private async apiGet<T = any>(paramsObj: Record<string, string>): Promise<ApiResp<T>> {
    const params = new HttpParams({ fromObject: paramsObj });
    return await firstValueFrom(this.http.get<ApiResp<T>>(this.API_URL, { params }));
  }

  private async apiPost<T = any>(body: any): Promise<ApiResp<T>> {
    return await firstValueFrom(this.http.post<ApiResp<T>>(this.API_URL, body));
  }

  async refreshData() {
    this.loading = true;
    this.errorMessage = '';
    this.infoMessage = '';
    
    try {
      const resp = await this.apiGet<ContactAccessProfile[]>({ request_type: 'profiles_list' });
      if (!resp || !resp.success) { 
        this.errorMessage = resp?.message || 'Failed to load profiles.'; 
        return; 
      }
      
      this.profiles = resp.data || [];
      
      if (this.selectedProfile?.contact_access_profile_uuid && this.selectedProfile.contact_access_profile_uuid !== 'NEW') {
        await this.loadProfileDetail(this.selectedProfile.contact_access_profile_uuid);
      } else if (this.profiles.length > 0) {
        await this.loadProfileDetail(this.profiles[0].contact_access_profile_uuid);
      } else {
        this.selectedProfile = null;
        this.fieldRowData = [];
        this.applyFieldRowDataToGrid();
      }
    } catch (err: any) { 
      this.errorMessage = 'Network error loading profiles.'; 
    } finally { 
      this.loading = false; 
    }
  }

  async selectProfile(p: ContactAccessProfile) {
    if (!p?.contact_access_profile_uuid) return;
    if (p.contact_access_profile_uuid === 'NEW') {
      this.selectedProfile = p;
      this.profileForm.patchValue(p, { emitEvent: false });
      this.fieldRowData = [];
      this.applyFieldRowDataToGrid();
      return;
    }
    await this.loadProfileDetail(p.contact_access_profile_uuid);
  }

  private async loadProfileDetail(uuid: string) {
    this.loading = true;
    this.errorMessage = '';
    
    try {
      const resp = await this.apiGet<ContactAccessProfile>({ request_type: 'profile_detail', contact_access_profile_uuid: uuid });
      if (!resp || !resp.success || !resp.data) {
        this.errorMessage = resp?.message || 'Failed to load profile detail.';
        return;
      }
      
      this.selectedProfile = resp.data as any;

      this.profileForm.reset({
          profile_name: this.selectedProfile?.profile_name,
          entity_target: this.selectedProfile?.entity_target,
          base_view: this.selectedProfile?.base_view,
          profile_type: (this.selectedProfile?.profile_type ?? null) as any,
          contact_uuid: this.selectedProfile?.contact_uuid ?? null,
          active: !!this.selectedProfile?.active,
          is_default: !!this.selectedProfile?.is_default,
        }, { emitEvent: false }
      );

      const cfg = Array.isArray(this.selectedProfile?.field_config) ? this.selectedProfile?.field_config : [];
      this.fieldRowData = cfg
        .map((x: any) => ({ ...x }))
        .sort((a: FieldConfigItem, b: FieldConfigItem) => (Number(a.display_order ?? 0) - Number(b.display_order ?? 0))) as FieldConfigItem[];
      this.applyFieldRowDataToGrid();
    } catch (err: any) {
      this.errorMessage = 'Network error loading profile detail.';
    } finally {
      this.loading = false;
    }
  }

  createNewProfileStub() {
    this.errorMessage = '';
    this.infoMessage = 'New profile stub created (not saved yet).';
    
    const stub: ContactAccessProfile = {
      contact_access_profile_uuid: 'NEW', domain_uuid: '', contact_uuid: null, profile_type: null,
      entity_target: 'work_orders', profile_name: 'New Profile', is_default: false, active: true, 
      field_config: [], created: '', modified: '', base_view: 'v_ladings',
    };
    
    this.profiles = [stub, ...this.profiles];
    this.selectProfile(stub);
  }

  addFieldRow() {
    this.syncFieldRowDataFromGrid(); // Prevent losing unsaved grid edits
    this.fieldRowData = [...this.fieldRowData, {
      db_field: '', input_type: 'readonly_text', is_visible: true, header_name: '', is_editable: false, is_sortable: true, display_order: 0,
    }];
    this.renumberFieldDisplayOrders();
    this.applyFieldRowDataToGrid();
  }

  private removeFieldRow(idx: number) {
    if (idx < 0 || idx >= this.fieldRowData.length) return;
    this.syncFieldRowDataFromGrid(); // Prevent losing unsaved grid edits
    const copy = [...this.fieldRowData];
    copy.splice(idx, 1);
    this.fieldRowData = copy;
    this.renumberFieldDisplayOrders();
    this.applyFieldRowDataToGrid();
  }

  private applyFieldRowDataToGrid() {
    if (!this.fieldGridApi) return;
    this.fieldGridApi.setGridOption('rowData', this.fieldRowData);
  }

  private syncFieldRowDataFromGrid() {
    if (!this.fieldGridApi) return;
    const rows: FieldConfigItem[] = [];
    this.fieldGridApi.forEachNode(n => { if (n.data) rows.push({...n.data}); });
    this.fieldRowData = rows;
  }

  private renumberFieldDisplayOrders(rows: FieldConfigItem[] = this.fieldRowData): FieldConfigItem[] {
    rows.forEach((row, index) => {
      row.display_order = (index + 1) * this.displayOrderStep;
    });
    return rows;
  }

  private collectFieldConfigForSave(): FieldConfigItem[] {
    this.fieldGridApi?.stopEditing();
    if (!this.fieldGridApi) {
      return this.renumberFieldDisplayOrders((this.fieldRowData || []).map((x) => ({ ...x })));
    }

    const rows: FieldConfigItem[] = [];
    this.fieldGridApi.forEachNode((n) => { if (n.data) rows.push({ ...n.data }); });
    return this.renumberFieldDisplayOrders(rows);
  }

  async saveProfile() {
    this.errorMessage = '';
    this.infoMessage = '';

    if (!this.selectedProfile) { this.errorMessage = 'Select a profile first.'; return; }
    if (this.profileForm.invalid) { this.errorMessage = 'Missing required fields.'; return; }

    const form = this.profileForm.getRawValue();
    const cfg: FieldConfigItem[] = this.collectFieldConfigForSave();

    const bodyBase = {
      profile_name: String(form.profile_name ?? '').trim(),
      entity_target: String(form.entity_target ?? '').trim(),
      base_view: String(form.base_view ?? '').trim(),
      profile_type: form.profile_type ?? null,
      contact_uuid: form.contact_uuid ?? null,
      active: !!form.active,
      is_default: !!form.is_default,
      field_config: cfg,
    };

    this.saving = true;
    try {
      if (this.selectedProfile.contact_access_profile_uuid === 'NEW') {
        const resp = await this.apiPost({ action: 'create_profile', ...bodyBase });
        if (!resp || !resp.success) { this.errorMessage = resp?.message || 'Create failed.'; return; }
        this.infoMessage = 'Profile created.';
        await this.refreshData();
        await this.loadProfileDetail(resp.contact_access_profile_uuid!);
        return;
      }

      const resp = await this.apiPost({ action: 'save_profile', contact_access_profile_uuid: this.selectedProfile.contact_access_profile_uuid, ...bodyBase });
      if (!resp || !resp.success) { this.errorMessage = resp?.message || 'Save failed.'; return; }
      this.infoMessage = 'Profile saved.';
      await this.refreshData();
    } catch (err: any) {
      this.errorMessage = 'Network error saving profile.';
    } finally {
      this.saving = false;
    }
  }

  private stringifyOrEmpty(v: any): string { 
    if (v === null || v === undefined || v === '') return '';
    return typeof v === 'string' ? v : JSON.stringify(v); 
  }
  
  private parseJsonOrKeepString(v: any): any { 
    if (v === null || v === undefined) return v;
    if (typeof v !== 'string') return v;
    const s = v.trim();
    if (s === '') return null;
    if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
      try { return JSON.parse(s); } catch { return v; }
    }
    return v;
  }
}